import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import upload from "../../../assets/images/CloudUpload.svg";
import DeleteIcon from "../../../assets/images/deleteIconRed.svg";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { showToast } from "../toastNotification";

interface VideoFileUploadProps {
  onFileSelect: (file: File | null) => void;
  isYouTubeLink?: boolean;
  videoFilePath?: string;
}

const VideoFileUpload: React.FC<VideoFileUploadProps> = ({
  onFileSelect,
  isYouTubeLink,
  videoFilePath,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else if (videoFilePath && !isYouTubeLink) {
      setPreviewUrl(videoFilePath);
    } else {
      setPreviewUrl(null);
    }

    // Cleanup URL object
    return () => {
      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [file, videoFilePath, isYouTubeLink]);

  const handleDelete = () => {
    setFile(null);
    setPreviewUrl(null);
    onFileSelect(null);
  };

  const allowedVideoTypes = ["video/mp4", "video/webm", "video/ogg"];
  const isValidVideoFile = (files: File) => {
    return allowedVideoTypes.includes(files.type);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0] || null;
    if (uploadedFile && isValidVideoFile(uploadedFile)) {
      setFile(uploadedFile);
      onFileSelect(uploadedFile);
    } else {
      showToast("Please upload a valid video file.", "error");
      setFile(null);
      onFileSelect(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      if (isValidVideoFile(droppedFile)) {
        setFile(droppedFile);
        onFileSelect(droppedFile);
      } else {
        showToast("Please upload a valid video file.", "error");
        setFile(null);
        onFileSelect(null);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          border: "2px dashed #CCCCCC",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragging ? "#f1f1f1" : "#212121 !important",
          maxWidth: "750px",
          margin: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "234px",
          transition: "background-color 0.2s",
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="video/mp4,video/webm,video/ogg"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <img src={upload} alt="Upload Icon" />

        <Typography
          variant="h6"
          sx={{ mt: 2, fontSize: "16px", fontWeight: "500" }}
        >
          Drop your Video here or{" "}
          <Box
            component="span"
            sx={{
              color: "red",

              cursor: "pointer",
              fontFamily: "Montserrat",
            }}
          >
            browse
          </Box>
        </Typography>

        <Typography
          variant="body2"
          color="#CCCCCC"
          sx={{ fontSize: "14px", mt: 1, color: "#E6E6E6" }}
        >
          Max. File Size: 200MB
        </Typography>
      </Box>
      {previewUrl && !isYouTubeLink && (
        <List sx={{ mt: 1, width: "100%" }}>
          <ListItem sx={{ borderBottom: "1px solid #808080", paddingX: 0 }}>
            <Box
              sx={{
                padding: "8px 10px",
                bgcolor: "#5D5D5D",
                mr: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <VideoLibraryIcon sx={{ color: "white", fontSize: 24 }} />
            </Box>

            <ListItemText
              sx={{
                "& .MuiTypography-root": {
                  color: "#CCCCCC !important",
                },
              }}
              primary={
                file?.name ||
                videoFilePath?.split("/").pop() ||
                "Existing Video"
              }
            />

            <IconButton edge="end" onClick={handleDelete}>
              <img
                src={DeleteIcon}
                alt="Delete"
                width={20}
                height={20}
                style={{ marginRight: "10px" }}
              />
            </IconButton>
          </ListItem>
        </List>
      )}
    </>
  );
};

export default VideoFileUpload;
